import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { CrmActivitiesTab, canReadActivities } from '@/Containers/CrmSection/Components/Activities'
import { CrmCommentsAside } from '@/Containers/CrmSection/Components/Comments'
import { CrmContactsTab } from '@/Containers/CrmSection/Components/Contacts'
import { CrmContractsTab } from '@/Containers/CrmSection/Components/Contracts'
import { CrmDocumentsTab } from '@/Containers/CrmSection/Components/Documents'
import { CrmHistoryTab } from '@/Containers/CrmSection/Components/History'
import { CrmLettersTab, canReadLetters } from '@/Containers/CrmSection/Components/Letters'
import { ApplyCrmOwnerCreateDefaultsTask } from '@/Containers/CrmSection/Components/Owner/ApplyCrmOwnerCreateDefaultsTask'
import { CrmStatusCell } from '@/Containers/CrmSection/Components/Owner/CrmStatusCell'
import { VerificationRequirementsAlert } from '@/Containers/CrmSection/Components/VerificationButtons'
import { CarrierApplicationsTab, canReadApplications } from '@/Containers/TransportationSection/ApplicationContainer/Hooks'
import { Form, FormField } from '@/Core/UI/Form'
import { CostCurrencyField } from '@/Ship/Components/CostCurrencyField'
import {
    EntityBody,
    EntityLayout,
    EntityPanel,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
    FileFieldCell,
} from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'

import { CarrierCardHeader } from '../Components/CarrierCardHeader'
import { CARRIER_CONTACT_COLUMNS } from '../Contracts/CarrierContactFieldLabels'
import { CARRIER_COST_FIELDS, CARRIER_FIELD_LABELS } from '../Contracts/CarrierFieldLabels'
import { CARRIER_FILE_CATEGORY_LABELS, CARRIER_FILE_FIELD_NAMES } from '../Contracts/CarrierFileCategory'
import { CARRIER_HISTORY_EVENT_MAP } from '../Contracts/CarrierHistoryEvent'
import {
    CARRIER_COMMENT_REST_URL,
    CARRIER_CONTACT_RESOURCE,
    CARRIER_CONTACT_REST_URL,
    CARRIER_CONTRACT_RESOURCE,
    CARRIER_CONTRACT_REST_URL,
    CARRIER_DOCUMENT_RESOURCE,
    CARRIER_DOCUMENT_REST_URL,
    CARRIER_REST_URL,
} from '../Contracts/ICarrier'

import type { CarrierCrudContextLike, CarrierFormContextLike } from '../Contracts/ICarrier'
import type { ReactNode } from 'react'

interface Props {
    crudContext: CarrierCrudContextLike | undefined
    formContext: CarrierFormContextLike | undefined
}

/** Місце статусу в блоці: його малює не `FormField`, а `CrmStatusCell`. */
const STATUS = 'status'

/**
 * Блоки «Загального» (`SPEC-010` §9.4). Порядок полів усередині блоків — як
 * у легасі (`CarrierFormTemplate.jsx:28-53`, `:80-106`); назви «Транспорт»,
 * «Реквізити», «Відповідальний і статус» — за дизайном карток.
 *
 * «Контактна особа, з ким ведуться перемовини» — селект контактів **цього**
 * перевізника (R-08), лише в збереженого: у новому контактів ще немає.
 */
const SECTIONS = {
    main: ['title', 'fullName', 'edrpou', 'phones', 'emails', 'lardi', 'comment', 'tags'],
    transport: ['carTypes', 'carParkSize', 'countries'],
    requisites: ['formTaxation', 'address', 'iban', 'bank', 'mfo', 'ipn', 'numberVAT', 'manager'],
    responsible: ['responsibles', STATUS],
    verification: [
        'forwarder',
        'contactPerson',
        'carOwnershipType',
        'isCoincidenceCarRegistration',
        'isInspectionEdrpou',
        'isCarInspectionOpendatabot',
        'lardiChanges',
        'numberPositiveReviews',
        'numberNegativeReviews',
        'mentionInGoogle',
        'approximateCostCargo',
        'cmrInsurance',
        'recommendation1',
        'recommendation2',
        'recommendation3',
    ],
    verified: ['verifiedBy', 'verifiedAt', 'commentVerification'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: довгі тексти (`SPEC-010` §9.4). */
const WIDE = new Set<string>(['comment', 'address', 'commentVerification'])

/**
 * Не у формі створення: статус, верифікацію й аудит ставить сервер, а
 * контактну особу нема з кого вибрати — контактів у нового перевізника ще
 * немає (R-08).
 */
const SAVED_ONLY_FIELDS = new Set<string>([STATUS, 'contactPerson', ...SECTIONS.verified, ...SECTIONS.service])

/** Сума → валюта: пару малює один `CostCurrencyField`. */
const COST_BY_AMOUNT = new Map<string, string>(CARRIER_COST_FIELDS.map(({ amount, currency }) => [amount, currency]))

const CURRENCY_FIELDS: readonly string[] = CARRIER_COST_FIELDS.map(({ currency }) => currency)

/**
 * Розставлене по блоках, а також те, що в картці не показується: «Ід» —
 * колонка й фільтр реєстру, а не поле картки.
 */
const PLACED = new Set<string>([
    ...Object.values(SECTIONS).flat(),
    ...CURRENCY_FIELDS,
    ...CARRIER_FILE_FIELD_NAMES,
    'id',
])

/**
 * Значення за замовчуванням форми створення — один раз на відкриту форму:
 * відповідальний — той, хто створює (R-07), валюта суми — EUR (R-02).
 * Окремий компонент без рендера: шаблон значень форми не читає.
 */
const CarrierCreateDefaults = ({ formContext }: { formContext: CarrierFormContextLike }) => {
    useEffect(() => {
        void ApplyCrmOwnerCreateDefaultsTask(formContext, CURRENCY_FIELDS)
    }, [formContext])

    return null
}

/**
 * Картка перевізника на спільних правилах карток (`EntityLayout`,
 * `SPEC-008` §7.1, `SPEC-010` §9.3–§9.4): шапка, вкладки, блоки полів двома
 * стовпцями.
 *
 * Файли — чотири поля за категоріями в блоці «Верифікація»: у перегляді —
 * посилання, у формі — ядровий `FileInput`, який вантажить файл одразу
 * сиротою, а «Зберегти» прикріплює його (`attachUploadedFiles` ресурсу). Тож
 * файли можна додати вже у формі створення (R-11).
 *
 * Праворуч — компактні «Контакти» (списком карток) і стрічка коментарів перевізника
 * (`CrmCommentsAside`); ліворуч першим блоком — компактні «Справи» (2026-10-14). У формі
 * створення компактних блоків немає, а на місці стрічки — пояснення, коли
 * вона з'явиться.
 *
 * Вкладки — лише в збереженого перевізника: «Загальне», «Контакти»,
 * «Договори», «Друковані форми» (лише верифікованому, R-13), «Листи» (S8b,
 * вміст — `LetterContainer/Hooks`), «Справи» (S6a, вміст —
 * `ActivityContainer/Hooks`), «Історія змін» — остання.
 *
 * Після 422 «Відправити на верифікацію» картка відкривається в редагуванні:
 * зверху — перелік незаповненого, під кожним із 20 полів — текст бека,
 * зокрема під файловими (`VerificationRequirementsAlert`, §6.1).
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid` і
 * обчислюваний `isVerified` (`SPEC-002` §4.2): інакше ядровий `FormField`
 * перемонтовував би всі поля на кожну літеру. Живі значення показують окремі observer-компоненти
 * (`CarrierCardHeader`, `CrmStatusCell`, `CostCurrencyField`, `FileFieldCell`).
 */
export const CarrierFormTemplate = observer(({ crudContext, formContext }: Props) => {
    // Лічильники відкриттів вкладок-списків: antd тримає вкладку змонтованою,
    // і без перемонтування вона не побачила б щойно збережених змін. Стрічку
    // коментарів не перемонтовуємо — щоб не губити чернетку
    const [historyEpoch, setHistoryEpoch] = useState(0)
    const [contactsEpoch, setContactsEpoch] = useState(0)
    const [contractsEpoch, setContractsEpoch] = useState(0)
    const [documentsEpoch, setDocumentsEpoch] = useState(0)
    const [activitiesEpoch, setActivitiesEpoch] = useState(0)
    const [applicationsEpoch, setApplicationsEpoch] = useState(0)
    const [lettersEpoch, setLettersEpoch] = useState(0)
    // Компактні блоки «Загального» перечитуються при поверненні на вкладку:
    // контакт чи справу могли змінити на сусідніх вкладках
    const [generalEpoch, setGeneralEpoch] = useState(0)

    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''
    // Обчислюване зі статусу: у формі його не правлять
    const isVerified = formContext?.values?.isVerified === true

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode => {
        if (isNew && SAVED_ONLY_FIELDS.has(name)) return null

        if (name === STATUS) {
            return formContext ? (
                <FieldCell key={name}>
                    <CrmStatusCell formContext={formContext} label={CARRIER_FIELD_LABELS.status} />
                </FieldCell>
            ) : null
        }

        const currency = COST_BY_AMOUNT.get(name)

        if (currency !== undefined) {
            return formContext && name in fields && currency in fields ? (
                <FieldCell key={name}>
                    <CostCurrencyField
                        formContext={formContext}
                        amount={name}
                        currency={currency}
                        label={CARRIER_FIELD_LABELS[name as keyof typeof CARRIER_FIELD_LABELS]}
                    />
                </FieldCell>
            ) : null
        }

        if (!(name in fields)) return null

        return (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        )
    }

    const grid = (names: readonly string[]) => (
        <Form formContext={formContext}>
            <FieldGrid>{names.map(cell)}</FieldGrid>
        </Form>
    )

    // Кожне поле — на всю ширину: файлів у категорії буває кілька
    const files = (
        <Form formContext={formContext}>
            <FieldGrid>
                {CARRIER_FILE_FIELD_NAMES.filter((name) => name in fields).map((name) => (
                    <FieldCell key={name} wide>
                        <FileFieldCell formContext={formContext} name={name} label={CARRIER_FIELD_LABELS[name]} />
                    </FieldCell>
                ))}
            </FieldGrid>
        </Form>
    )

    const main = (
        <>
            {/* Після 422 «Відправити на верифікацію» картка відкривається в
                редагуванні: перелік незаповненого — першим, бо блок
                «Верифікація» лежить нижче першого екрана (§6.1) */}
            {formContext && !isNew ? (
                <VerificationRequirementsAlert
                    formContext={formContext}
                    restUrl={crudContext?.resource?.restUrl ?? CARRIER_REST_URL}
                    fieldLabels={CARRIER_FIELD_LABELS}
                />
            ) : null}
            {/* Компактні «Справи» — у широкій лівій колонці: у вузькій правій
                таблиця з типом, терміном, відповідальним і статусом не вміщалася
                (замовник, 2026-10-14; `SPEC-011` §8.6 В3, В10) — лише в
                збереженого власника й тому, хто бачить справи */}
            {formContext && !isNew && canReadActivities() ? (
                <FieldSection title="Справи">
                    <CrmActivitiesTab
                        key={generalEpoch}
                        mode="compact"
                        owner="carrier"
                        ownerFormContext={formContext}
                    />
                </FieldSection>
            ) : null}
            <FieldSection title="Головне">{grid(SECTIONS.main)}</FieldSection>
            <FieldSection title="Транспорт">{grid(SECTIONS.transport)}</FieldSection>
            <FieldSection title="Реквізити">{grid(SECTIONS.requisites)}</FieldSection>
            <FieldSection title="Відповідальний і статус">
                <Hint id="crm.carrier.responsible">
                    Логіст без інших ролей змінює лише тих перевізників, де він серед відповідальних, і не може
                    прибрати себе з відповідальних.
                </Hint>
                {grid(SECTIONS.responsible)}
            </FieldSection>
            <FieldSection title="Верифікація">
                <Hint id="crm.carrier.verification">
                    Для відправки на верифікацію потрібні 20 вимог: обов'язкові поля, три позначені перевірки
                    (реєстрація авто, ЄДРПОУ, OpenDataBot) і хоча б по одному файлу «Реєстраційний документ» і
                    «Технічний паспорт». Контактну особу обирають із контактів перевізника, тож спершу додайте контакт.
                </Hint>
                {grid(SECTIONS.verification)}
                <Hint id="crm.carrier.files">
                    Завантажені файли прикріплюються до перевізника лише після натискання «Зберегти» — без
                    збереження вони в картці не лишаться.
                </Hint>
                {files}
                {isNew ? null : grid(SECTIONS.verified)}
            </FieldSection>
            <FieldSection title="Додатково" hidden={rest.length === 0}>
                {grid(rest)}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    const aside = (
        <>
            {/* Компактні «Контакти» — праворуч над коментарями, списком карток
                (замовник, 2026-10-14: поміняти місцями зі «Справами»); лише в
                збереженого власника */}
            {formContext && !isNew ? (
                <FieldSection title="Контакти">
                    <CrmContactsTab
                        key={generalEpoch}
                        mode="compact"
                        resource={CARRIER_CONTACT_RESOURCE}
                        restUrl={CARRIER_CONTACT_REST_URL}
                        ownerField="carrier"
                        ownerFormContext={formContext}
                        columns={CARRIER_CONTACT_COLUMNS}
                    />
                </FieldSection>
            ) : null}
            <CrmCommentsAside
                restUrl={CARRIER_COMMENT_REST_URL}
                ownerField="carrier"
                formContext={formContext}
                unsavedText="Коментувати можна буде після збереження перевізника."
            />
        </>
    )

    if (isNew || !formContext) {
        return (
            <EntityLayout>
                {formContext ? <CarrierCreateDefaults formContext={formContext} /> : null}
                <EntityBody main={main} aside={aside} />
            </EntityLayout>
        )
    }

    return (
        <EntityLayout>
            <EntityTabs
                header={<CarrierCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === 'general') setGeneralEpoch((epoch) => epoch + 1)
                    if (key === 'contacts') setContactsEpoch((epoch) => epoch + 1)
                    if (key === 'contracts') setContractsEpoch((epoch) => epoch + 1)
                    if (key === 'documents') setDocumentsEpoch((epoch) => epoch + 1)
                    if (key === 'letters') setLettersEpoch((epoch) => epoch + 1)
                    if (key === 'activities') setActivitiesEpoch((epoch) => epoch + 1)
                    if (key === 'applications') setApplicationsEpoch((epoch) => epoch + 1)
                    if (key === 'history') setHistoryEpoch((epoch) => epoch + 1)
                }}
                items={[
                    { key: 'general', label: 'Загальне', children: <EntityBody main={main} aside={aside} /> },
                    {
                        key: 'contacts',
                        label: 'Контакти',
                        children: (
                            <EntityPanel>
                                <CrmContactsTab
                                    key={contactsEpoch}
                                    resource={CARRIER_CONTACT_RESOURCE}
                                    restUrl={CARRIER_CONTACT_REST_URL}
                                    ownerField="carrier"
                                    ownerFormContext={formContext}
                                    columns={CARRIER_CONTACT_COLUMNS}
                                />
                            </EntityPanel>
                        ),
                    },
                    {
                        key: 'contracts',
                        label: 'Договори',
                        children: (
                            <EntityPanel>
                                <CrmContractsTab
                                    key={contractsEpoch}
                                    resource={CARRIER_CONTRACT_RESOURCE}
                                    restUrl={CARRIER_CONTRACT_REST_URL}
                                    ownerField="carrier"
                                    ownerFormContext={formContext}
                                />
                            </EntityPanel>
                        ),
                    },
                    // «Друковані форми» — лише верифікованому перевізнику (R-13):
                    // генерація бере дані верифікації й договору
                    ...(isVerified
                        ? [
                              {
                                  key: 'documents',
                                  label: 'Друковані форми',
                                  children: (
                                      <EntityPanel>
                                          <CrmDocumentsTab
                                              key={documentsEpoch}
                                              resource={CARRIER_DOCUMENT_RESOURCE}
                                              restUrl={CARRIER_DOCUMENT_REST_URL}
                                              ownerField="carrier"
                                              ownerFormContext={formContext}
                                          />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    // «Листи» (`SPEC-013` §9.2, S8b) — лише тому, хто бачить листи;
                    // вміст — `LetterContainer/Hooks`
                    ...(canReadLetters()
                        ? [
                              {
                                  key: 'letters',
                                  label: 'Листи',
                                  children: (
                                      <EntityPanel>
                                          <CrmLettersTab
                                              key={lettersEpoch}
                                              owner="carrier"
                                              ownerFormContext={formContext}
                                          />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    // «Справи» (`SPEC-010` §9.4, `SPEC-011` §8.6) —
                    // лише тому, хто бачить справи (В4)
                    ...(canReadActivities()
                        ? [
                              {
                                  key: 'activities',
                                  label: 'Справи',
                                  children: (
                                      <EntityPanel>
                                          <CrmActivitiesTab
                                              key={activitiesEpoch}
                                              owner="carrier"
                                              ownerFormContext={formContext}
                                          />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    // «Заявки» перевізника (`SPEC-025` §3.5; легасі
                    // `CarrierFormTemplate.jsx:160-176`) — лише тому, хто бачить заявки
                    ...(canReadApplications()
                        ? [
                              {
                                  key: 'applications',
                                  label: 'Заявки',
                                  children: (
                                      <EntityPanel>
                                          <CarrierApplicationsTab key={applicationsEpoch} record={formContext?.values} />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    {
                        key: 'history',
                        label: 'Історія змін',
                        children: (
                            <EntityPanel>
                                <CrmHistoryTab
                                    key={historyEpoch}
                                    restUrl={crudContext?.resource?.restUrl ?? CARRIER_REST_URL}
                                    uuid={uuid}
                                    events={CARRIER_HISTORY_EVENT_MAP}
                                    fileCategories={CARRIER_FILE_CATEGORY_LABELS}
                                />
                            </EntityPanel>
                        ),
                    },
                ]}
            />
        </EntityLayout>
    )
})
